import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface EmailSubscription {
  id: string;
  user_id: string;
  subscription_type: string;
  is_subscribed: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Hook to fetch email subscriptions for the current user
 */
export const useEmailSubscriptions = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["email-subscriptions", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("email_subscriptions")
        .select("*")
        .eq("user_id", user.id);

      if (error) {
        console.error("Error fetching email subscriptions:", error);
        throw error;
      }

      return data as EmailSubscription[];
    },
    enabled: !!user?.id,
  });
};

/**
 * Hook to check if user is subscribed to a specific type
 */
export const useIsSubscribed = (subscriptionType: string) => {
  const { data: subscriptions } = useEmailSubscriptions();
  
  const subscription = subscriptions?.find(s => s.subscription_type === subscriptionType);
  return subscription?.is_subscribed ?? false; // Default to unsubscribed - opt-in model
};

/**
 * Hook to toggle email subscription
 */
export const useToggleEmailSubscription = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ subscriptionType, isSubscribed }: { subscriptionType: string; isSubscribed: boolean }) => {
      if (!user?.id) throw new Error("User not authenticated");

      // Upsert subscription
      const { error } = await supabase
        .from("email_subscriptions")
        .upsert({
          user_id: user.id,
          subscription_type: subscriptionType,
          is_subscribed: isSubscribed,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,subscription_type'
        });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["email-subscriptions", user?.id] });
      toast.success(
        variables.isSubscribed
          ? "Email notifications enabled"
          : "Email notifications disabled"
      );
    },
    onError: (error) => {
      console.error("Error updating subscription:", error);
      toast.error("Failed to update email preferences");
    },
  });
};
